import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/store/auth";
import { fmtDate, StatusBadge, PriorityBadge } from "@/components/common";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, Clock, Hourglass,
  FileCheck, TrendingUp, ChevronRight, RefreshCw,
} from "lucide-react";

export default function MobileDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/tasks?limit=5"),
      ]);
      setSummary(s.data?.data || {});
      setRecent(t.data?.data || []);
    } catch {}
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(); };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const KPI_CARDS = summary ? [
    { label: "Total Tasks", value: summary.total_tasks ?? 0, icon: TrendingUp, color: "text-slate-900", bg: "bg-slate-50" },
    { label: "Pending", value: summary.pending_tasks ?? 0, icon: Hourglass, color: "text-[#334E7B]", bg: "bg-blue-50" },
    { label: "Delayed", value: summary.delayed_tasks ?? 0, icon: AlertTriangle, color: "text-red-700", bg: "bg-red-50" },
    { label: "Completed", value: summary.completed_tasks ?? 0, icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50" },
    { label: "Approval Pending", value: summary.awaiting_approval ?? 0, icon: FileCheck, color: "text-violet-700", bg: "bg-violet-50" },
    { label: "Due Today", value: summary.today_tasks ?? 0, icon: Clock, color: "text-amber-700", bg: "bg-amber-50" },
  ] : [];

  if (loading) return (
    <div className="flex flex-col gap-4 p-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-20 bg-slate-200 rounded-2xl animate-pulse" />
      ))}
    </div>
  );

  return (
    <div className="pb-4">
      {/* Greeting header */}
      <div className="px-4 pt-5 pb-3 flex items-start justify-between">
        <div>
          <div className="text-[13px] text-slate-500 font-medium">{greeting()},</div>
          <div className="text-xl font-heading font-bold text-slate-900">{user?.name?.split(" ")[0]}</div>
          <div className="text-xs text-slate-400 mt-0.5">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
        <button onClick={refresh} className={`p-2 rounded-full bg-slate-100 ${refreshing ? "animate-spin" : ""}`}>
          <RefreshCw size={16} className="text-slate-500" />
        </button>
      </div>

      {/* KPI horizontal scroll */}
      <div className="px-4 mb-4">
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
          {KPI_CARDS.map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`flex-shrink-0 snap-start w-32 ${bg} rounded-2xl p-3.5 border border-white/60`}>
              <Icon size={18} className={color} strokeWidth={2} />
              <div className={`text-2xl font-heading font-bold mt-2 ${color}`}>{value}</div>
              <div className="text-[11px] text-slate-500 font-medium mt-0.5 leading-tight">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div className="px-4 mb-5">
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate("/m/tasks")}
            className="bg-[#334E7B] text-white rounded-2xl p-4 flex items-center gap-3 active:scale-95 transition-transform">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp size={18} strokeWidth={2} />
            </div>
            <div className="text-left">
              <div className="text-xs font-medium opacity-80">View All</div>
              <div className="text-sm font-bold">Tasks</div>
            </div>
          </button>
          <button onClick={() => navigate("/m/inbox")}
            className="bg-violet-600 text-white rounded-2xl p-4 flex items-center gap-3 active:scale-95 transition-transform">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <FileCheck size={18} strokeWidth={2} />
            </div>
            <div className="text-left">
              <div className="text-xs font-medium opacity-80">Pending</div>
              <div className="text-sm font-bold">Approvals</div>
            </div>
          </button>
        </div>
      </div>

      {/* Recent tasks */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-heading font-bold text-slate-800">Recent Tasks</div>
          <button onClick={() => navigate("/m/tasks")} className="text-xs text-[#334E7B] font-semibold flex items-center gap-0.5">
            See all <ChevronRight size={13} />
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">No tasks yet</div>
        ) : (
          <div className="space-y-2">
            {recent.map(t => (
              <button key={t.id} onClick={() => navigate("/m/tasks")}
                className="w-full bg-white rounded-2xl p-3.5 flex items-start gap-3 text-left border border-slate-100 active:bg-slate-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] text-slate-400">{t.task_code}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-sm font-semibold text-slate-900 truncate">{t.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                    <span>{t.assigned_to_name || "—"}</span>
                    {t.original_due_date && <span>· {fmtDate(t.original_due_date)}</span>}
                  </div>
                </div>
                <PriorityBadge priority={t.priority_name} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
